// @mui
import { Box, Stack, useTheme } from '@mui/material';
// utils
import { useTranslation } from 'react-i18next';
import { Card } from 'src/components/ui/card';
import { Badge } from './ui/badge';
import { WidgetState } from 'src/pages/Integrations/config';
import { Settings, Edit2 } from 'lucide-react';
import { Button } from './ui/button';
import { CSSProperties } from 'react';

export function WidgetStateBadge({ state }: { state: WidgetState }) {
  const { t } = useTranslation();
  switch (state) {
    case WidgetState.ACTIVE:
      return (
        <Badge className="bg-green-200 text-green-900 hover:bg-gray-300 shadow-none">
          {t('general.state.active')}
        </Badge>
      );
    case WidgetState.INACTIVE:
      return (
        <Badge className="bg-gray-300 text-gray-800 hover:bg-gray-300 shadow-none">
          {t('general.state.inactive')}
        </Badge>
      );

    default:
      console.warn('Non supported state');
      return null;
  }
}

// ----------------------------------------------------------------------

export default function WidgetCardItem({
  widgetId,
  title,
  customTitle,
  cover,
  coverStyle,
  state,
  onClick,
  onEditClick,
}: {
  widgetId: string;
  title: string;
  customTitle?: string;
  cover: string;
  coverStyle?: CSSProperties;
  state: WidgetState;
  onClick: () => void;
  onEditClick?: (widgetId: string, currentTitle: string, customTitle?: string) => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation();

  const displayTitle = customTitle || title;

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEditClick) {
      onEditClick(widgetId, title, customTitle);
    }
  };

  return (
    <Card
      className="group shadow-none cursor-pointer hover:bg-neutral-50 transition-colors duration-150 overflow-hidden"
      onClick={onClick}
    >
      <Box sx={{ position: 'relative' }}>
        <img
          className={`${
            state === WidgetState.INACTIVE ? 'grayscale' : ''
          } group-hover:grayscale-0 transition-all duration-150`}
          src={cover}
          alt={cover}
          style={{
            // filter: state === WidgetState.INACTIVE ? 'grayscale(100%)' : 'none',
            objectFit: 'contain',
            minHeight: '100%',
            ...coverStyle,
          }}
        />

        <div className="absolute bottom-1 right-1">
          <WidgetStateBadge state={state} />
        </div>
      </Box>
      <Box sx={{ px: 2 }} />

      <Stack
        direction={'column'}
        // alignItems={'center'}
        justifyContent={'space-between'}
        sx={{
          py: 2,
          px: 2,
          borderTop: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Stack
          width="100%"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          pb={1.5}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <p className="text-sm truncate flex-1" title={displayTitle}>
              {displayTitle}
            </p>
            {onEditClick && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={handleEditClick}
              >
                <Edit2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </Stack>

        <Stack direction="column" alignItems="flex-start" spacing={1}>
          <Stack flexDirection="row" gap={1} alignItems="center" onClick={onClick}>
            <Box>
              {widgetId && (
                <Button
                  className="rounded-full h-7 w-7 shadow-none"
                  variant="outline"
                  size="icon"
                  // customColor={theme.palette.primary}
                >
                  <Settings />
                </Button>
              )}
            </Box>
            <p className="text-xs text-gray-500">{t('integrations.steps.settings')}</p>
          </Stack>
          {/* <Stack
            role="button"
            flexDirection="row"
            gap={1}
            alignItems="center"
            onClick={(e) => {
              e.stopPropagation();
              if (widgetCode) {
                onCopy();
              }
            }}
          >
            <Box>
              {widgetCode && (
                <CopyToClipboard text={widgetCode} onCopy={onCopy}>
                  <Tooltip title={t('integrations.steps.copy') as string}>
                    <Button
                      className="rounded-full h-7 w-7 shadow-none"
                      variant="outline"
                      size="icon"
                      // customColor={theme.palette.primary}
                    >
                      <Code />
                    </Button>
                  </Tooltip>
                </CopyToClipboard>
              )}
            </Box>
            <p className="text-xs text-gray-500">{t('integrations.steps.code')}</p>
          </Stack> */}
        </Stack>
      </Stack>
    </Card>
  );
}
